import styled from "@emotion/styled";
import { useRouter } from "next/router";
import React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { buildLinkPath } from "@brexhq/next/src/components/pageBuilder/utils";
import { Link } from "@brexhq/next/src/components/shared/Link";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import { SanityButtonRowProps } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems";
import { AlignmentOptions } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowsModule";

interface ButtonRowProps extends Omit<SanityButtonRowProps, "_type" | "_key"> {
  theme: ModuleThemeType;
  alignment: AlignmentOptions;
}

const ButtonContainer = styled(Container)({
  "> a": {
    margin: 0,
  },
});

export const ButtonRow = ({
  gap,
  marginTop = "none",
  buttons,
  theme,
  alignment,
  stackVertically,
  maxWidth,
  isFullWidth,
  addRefParam = false,
}: ButtonRowProps) => {
  const mapAlignmentToFlex: Record<
    AlignmentOptions,
    React.CSSProperties["alignItems"]
  > = {
    left: "flex-start",
    center: "center",
    right: "flex-end",
  };

  const router = useRouter();
  const { standard } = getModuleThemeColors(theme);
  const maxWidthValue = Number(maxWidth) ? `${maxWidth}%` : "100%";

  return (
    <ButtonContainer
      maxWidth={{
        LRG: maxWidthValue,
      }}
      margin={[marginTop, 0, 0, 0]}
      display="flex"
      alignItems={
        stackVertically || isFullWidth
          ? mapAlignmentToFlex[alignment]
          : "center"
      }
      gap={gap}
      justifyContent={mapAlignmentToFlex[alignment]}
      flexWrap="wrap"
      direction={stackVertically || isFullWidth ? "column" : "row"}
      width="100%"
    >
      {buttons.map((button) => {
        const buttonClickAction = button?.onButtonClick;
        const encodedRef = encodeURIComponent(router.asPath.toLowerCase());
        const path = buildLinkPath({ link: button }) ?? "";
        const pathWithRef = router.asPath.includes("spend-trends")
          ? path.replace(/(^.*?)(#.*)?$/, `$1?ref=${encodedRef}$2`)
          : path.replace(/(^.*?)(#.*)?$/, '$1?ref="none"$2');

        if (buttonClickAction === "download") {
          return (
            <Link
              key={button._key}
              modifier={button.modifier}
              to={button.file?.asset?.url ?? ""}
              label={button.label}
              component="TextRows"
              theme={theme}
              color={standard.textPrimary}
              isFullWidth={isFullWidth}
              trackingId={button.trackingId}
              download
              target="_blank"
              rel="noopener noreferrer"
              icon={button.icon?.icon}
              withIcon={button.withIcon}
              iconColor={button.icon?.color}
            />
          );
        }

        return (
          <Link
            key={button._key}
            modifier={button.modifier}
            to={addRefParam ? pathWithRef : path}
            label={button.label}
            component="TextRows"
            theme={theme}
            color={
              button.useTextColorArrowLink
                ? standard.textPrimary
                : standard.accent
            }
            isFullWidth={isFullWidth}
            trackingId={button.trackingId}
            icon={button.icon?.icon}
            withIcon={button.withIcon}
            iconColor={button.icon?.color}
          />
        );
      })}
    </ButtonContainer>
  );
};
